import React, { useState, useRef, useCallback } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Upload, X, FileText, FileSpreadsheet, Archive } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FileUploadDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    title?: string
    description?: string
    accept?: string
    multiple?: boolean
    onUpload: (files: File[]) => void
    isUploading?: boolean
}

export function FileUploadDialog({
    open,
    onOpenChange,
    title = 'Import Data',
    description = 'Upload CSV or JSON files to import data.',
    accept = '.csv,.json,.zip',
    multiple = true,
    onUpload,
    isUploading = false,
}: FileUploadDialogProps) {
    const [files, setFiles] = useState<File[]>([])
    const [isDragActive, setIsDragActive] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragActive(false)
        const dropped = Array.from(e.dataTransfer.files)
        setFiles(prev => [...prev, ...dropped])
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragActive(true)
    }, [])

    const handleDragLeave = useCallback(() => {
        setIsDragActive(false)
    }, [])

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selected = Array.from(e.target.files)
            setFiles(prev => [...prev, ...selected])
        }
    }

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index))
    }

    const handleUpload = () => {
        if (files.length > 0) {
            onUpload(files)
        }
    }

    const handleClose = (nextOpen: boolean) => {
        if (!nextOpen) {
            setFiles([])
        }
        onOpenChange(nextOpen)
    }

    const getFileIcon = (name: string) => {
        if (name.endsWith('.csv')) return <FileSpreadsheet className="h-4 w-4 text-green-500" />
        if (name.endsWith('.zip')) return <Archive className="h-4 w-4 text-amber-500" />
        return <FileText className="h-4 w-4 text-blue-500" />
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                {/* Drop zone */}
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => inputRef.current?.click()}
                    className={cn(
                        'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 cursor-pointer transition-colors',
                        isDragActive
                            ? 'border-primary bg-primary/5'
                            : 'border-muted-foreground/25 hover:border-primary/50',
                    )}
                >
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground text-center">
                        Drag & drop files here, or <span className="text-primary font-medium">browse</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Supported: CSV, JSON, ZIP</p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        multiple={multiple}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                </div>

                {/* File list */}
                {files.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                        {files.map((file, idx) => (
                            <div
                                key={`${file.name}-${idx}`}
                                className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {getFileIcon(file.name)}
                                    <span className="text-sm truncate">{file.name}</span>
                                    <span className="text-xs text-muted-foreground flex-shrink-0">
                                        ({(file.size / 1024).toFixed(1)} KB)
                                    </span>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 flex-shrink-0"
                                    onClick={(e) => { e.stopPropagation(); removeFile(idx) }}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleClose(false)} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button onClick={handleUpload} disabled={files.length === 0 || isUploading}>
                        {isUploading ? 'Uploading…' : `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
